// This module imports nothing, and must not: scripts/build.ts loads it outside a browser to write every document's
// head and body and the sitemap, scripts/index-now.ts loads an older commit's copy to see what changed, and a module
// reaching for React, the icons or a utility chunk could be loaded by neither, so nothing here assumes a DOM.
// What is written from these words is page-document.ts's; this is only the words.

export const SITE_NAME = "utils+";

// Spelled absolute because a canonical, an og:url or a sitemap entry is read off a copy of the page somebody else
// fetched, where a path resolves against nothing.
export const SITE_ORIGIN = "https://utils.plus";

export const HOME_PATH = "/";
export const ATTRIBUTIONS_PATH = "/attributions";

// Every address the site answers for, and what the head should say about it. The registry's paths are typed as the
// keys of this object, so a utility added without an entry here is a compile error rather than a page with no words.
export const PAGE_META = {
  "/": {
    label: "Home",
    title: "Developer Tools That Run in Your Browser",
    description:
      "Encoders, hashes, key generators, JSON, JWT, diff, cron and more, running entirely in your browser. No server-side processing, no third-party requests, no tracking.",
    keywords: [
      "developer tools",
      "online developer utilities",
      "browser based tools",
      "offline dev tools",
      "privacy first tools",
      "no upload tools",
    ],
  },
  "/calculator": {
    label: "Calculator",
    title: "Programmer & Scientific Calculator",
    description:
      "Add, shift, rotate and mask 8- to 64-bit words in hex, octal or decimal, with every bit on screen and read as an IEEE 754 float — or switch to trigonometry.",
    keywords: [
      "programmer calculator",
      "hex calculator",
      "binary calculator",
      "bitwise calculator",
      "two's complement",
      "bit shift calculator",
      "ieee 754 converter",
      "float to hex",
      "double precision bits",
      "scientific calculator",
      "trigonometry calculator",
      "hex to decimal",
    ],
  },
  "/certificate": {
    label: "Certificate",
    title: "X.509 Certificate Decoder & Generator",
    description:
      "Decode a certificate, a signing request or an SSH key — names, expiry, fingerprints, chain order — or generate a self-signed one, a root CA, or one signed by it.",
    keywords: [
      "certificate decoder",
      "x509 decoder",
      "ssl certificate viewer",
      "csr decoder",
      "pem decoder",
      "certificate fingerprint",
      "certificate expiry checker",
      "certificate chain order",
      "ssh public key fingerprint",
      "does key match certificate",
      "self-signed certificate generator",
      "create root ca",
    ],
  },
  "/codec": {
    label: "Codec",
    title: "Base64, Base32, Hex, Gzip, Morse & ROT13 Codec",
    description:
      "Encode and decode Base64, Base32, hex, decimal, binary, NATO, Morse and gzip, or run text through ROT13, Caesar, Vigenère and XOR. Nothing leaves the browser.",
    keywords: [
      "base64 encoder",
      "base64 decoder",
      "url-safe base64",
      "base32 encoder",
      "crockford base32",
      "hex encoder",
      "text to binary",
      "morse code translator",
      "nato phonetic alphabet",
      "rfc 4648",
      "deflate base64",
      "gzip base64",
      "inflate zlib",
      "decode samlrequest",
      "rot13 decoder",
      "rot47",
      "caesar cipher solver",
      "vigenere cipher",
      "xor cipher",
      "single byte xor",
    ],
  },
  "/colour": {
    label: "Colour",
    title: "Colour Converter, Contrast & Palette",
    description:
      "Convert between hex, RGB, HSL, CMYK, LAB and OKLCH, check WCAG contrast, build a palette in OKLCH, and see any of it through colour blindness.",
    keywords: [
      "colour converter",
      "color converter",
      "hex to rgb",
      "rgb to hex",
      "hsl converter",
      "oklch",
      "oklab",
      "cmyk converter",
      "css color names",
      "colour picker",
      "wcag contrast checker",
      "contrast ratio",
      "colour palette generator",
      "complementary colours",
      "colour blindness simulator",
    ],
  },
  "/config": {
    label: "Config",
    title: "YAML, JSON, TOML & .env Converter",
    description:
      "Convert a configuration between YAML, JSON, TOML, .env and Java properties, keeping every value's type across the flat formats that have none.",
    keywords: [
      "yaml to json",
      "json to yaml",
      "yaml to toml",
      "toml to json",
      "json to env",
      "dotenv converter",
      "properties to yaml",
      "config file converter",
    ],
  },
  "/converter": {
    label: "Converter",
    title: "Unit Converter for Length, Mass & Data",
    description:
      "Convert distance, area, volume, mass, temperature, speed, data, energy, power and pressure between metric, imperial and SI units, to twelve digits.",
    keywords: [
      "unit converter",
      "metric to imperial",
      "length converter",
      "temperature converter",
      "celsius to fahrenheit",
      "kg to lbs",
      "km to miles",
      "data storage converter",
      "pressure converter",
    ],
  },
  "/countries": {
    label: "Countries",
    title: "ISO 3166 Country Code & Data Lookup",
    description:
      "Pick any of 250 countries and read back its ISO 3166 codes, calling code, capital, currencies, languages, land borders, area and name in two dozen languages.",
    keywords: [
      "country codes",
      "iso 3166",
      "alpha-2 country code",
      "alpha-3 country code",
      "country calling codes",
      "country currency codes",
      "country capital lookup",
      "country tld",
      "demonyms",
    ],
  },
  "/cron": {
    label: "Cron",
    title: "Cron Expression Builder & Parser",
    description:
      "Write or read a cron expression field by field, in Unix or Quartz flavour, and see the next runs in your own time zone or UTC before you schedule it.",
    keywords: [
      "cron expression",
      "crontab generator",
      "cron parser",
      "quartz cron",
      "cron schedule",
      "next run time",
      "cron syntax",
      "crontab editor",
    ],
  },
  "/cryptography": {
    label: "Cryptography",
    title: "AES, ChaCha, NaCl, PGP & age Encryption",
    description:
      "Encrypt and decrypt text or a file with AES-GCM, AES-CBC, AES-CTR, ChaCha20-Poly1305, NaCl secretbox and box, OpenPGP, or age with post-quantum keys.",
    keywords: [
      "encrypt text online",
      "decrypt text online",
      "aes encryption",
      "aes-gcm",
      "aes-cbc",
      "chacha20-poly1305",
      "xchacha20",
      "nacl secretbox",
      "tweetnacl box",
      "x25519",
      "pgp encrypt message",
      "age encryption",
      "age encrypt file",
      "decrypt age file",
      "post-quantum encryption",
      "ml-kem",
      "encrypt a file in the browser",
    ],
  },
  "/csv": {
    label: "CSV",
    title: "CSV Viewer, Parser & Data Table",
    description:
      "Paste or drop a CSV, TSV or any delimited file and read it as a sortable table beside its own text, with the delimiter worked out for you. Nothing is uploaded.",
    keywords: [
      "csv viewer",
      "csv to table",
      "tsv viewer",
      "delimited file viewer",
      "csv parser",
      "open csv in browser",
      "sort csv columns",
      "csv editor",
    ],
  },
  "/curl": {
    label: "curl",
    title: "curl Command Builder, Parser & Runner",
    description:
      "Build a curl command argument by argument, or paste one in and have it taken apart into fields you can edit — then Send it as a fetch and read what came back.",
    keywords: [
      "curl command builder",
      "curl command generator",
      "curl parser",
      "curl post json",
      "curl header",
      "curl options",
      "build curl request",
      "edit curl command",
      "run curl in browser",
      "curl to fetch",
      "online http client",
      "send http request",
      "rest client",
    ],
  },
  "/diff": {
    label: "Diff",
    title: "Text & Code Diff Checker",
    description:
      "Compare two texts side by side, with the changed words marked rather than the whole line and syntax highlighting for sixteen languages. Nothing is uploaded.",
    keywords: [
      "diff checker",
      "text compare",
      "code diff",
      "compare two files",
      "word level diff",
      "side by side diff",
      "online diff tool",
      "myers diff",
    ],
  },
  "/docx": {
    label: "DOCX",
    title: "DOCX Viewer & Word Document Editor",
    description:
      "Open a Word document and read it as the pages it prints on, or edit its text, tables and styles and save it back as .docx. Nothing is uploaded.",
    keywords: [
      "docx viewer",
      "docx editor",
      "word document editor",
      "open docx online",
      "edit word document",
      "view docx without word",
      "online word editor",
      "docx reader",
    ],
  },
  "/har": {
    label: "HAR",
    title: "HAR Viewer & HTTP Archive Analyser",
    description:
      "Open a HAR recording from Chrome, Firefox or Safari and read every request — headers, cookies, bodies and timings — filtered by any field. Nothing is uploaded.",
    keywords: [
      "har viewer",
      "har file analyzer",
      "http archive viewer",
      "har analyser",
      "network log viewer",
      "devtools har",
      "read har file",
      "har request filter",
      "http request timings",
      "waterfall timings",
    ],
  },
  "/hasher": {
    label: "Hasher",
    title: "Hash & Key Derivation Calculator",
    description:
      "Hash text or a file with MD5, SHA-1, SHA-2, SHA-3, BLAKE2, BLAKE3, CRC32, xxHash or MurmurHash, or derive a key with Argon2, bcrypt, scrypt or PBKDF2, in the tab.",
    keywords: [
      "hash calculator",
      "md5 generator",
      "sha256 hash",
      "sha512 hash",
      "sha-3 keccak",
      "blake3",
      "crc32 checksum",
      "xxhash",
      "file checksum",
      "sha256 file hash",
      "argon2",
      "bcrypt generator",
      "scrypt",
      "pbkdf2",
    ],
  },
  "/hex": {
    label: "Hex",
    title: "Hex Editor & Binary File Viewer",
    description:
      "Open any file and read every byte of it, in hex and as text, patch the bytes in place, search for a signature, then save the file back out. Nothing is uploaded.",
    keywords: [
      "hex editor",
      "hex viewer",
      "online hex editor",
      "binary file editor",
      "hex dump",
      "edit binary file",
      "file signature viewer",
      "magic number lookup",
      "byte editor",
      "data inspector",
      "hex to text",
      "patch a binary",
    ],
  },
  "/image": {
    label: "Image",
    title: "Image Converter, Resizer & EXIF Editor",
    description:
      "Open a picture, read and edit its EXIF and metadata, crop, resize, turn and colour it, then save it as PNG, JPEG, WebP or AVIF, or as a data URI. Nothing is uploaded.",
    keywords: [
      "image converter",
      "exif viewer",
      "exif editor",
      "remove exif data",
      "strip image metadata",
      "image resizer",
      "crop image online",
      "png to jpg",
      "jpg to webp",
      "image to data uri",
      "base64 image encoder",
      "photo metadata viewer",
      "gps location from photo",
      "compress image",
    ],
  },
  "/ip-address": {
    label: "IP Address",
    title: "IPv4, IPv6 & CIDR Subnet Calculator",
    description:
      "Work out subnet ranges, masks and host counts from any CIDR block, split it into smaller ones, convert an address to an integer, and expand or compress IPv6.",
    keywords: [
      "subnet calculator",
      "cidr calculator",
      "ip address calculator",
      "ipv4 subnet mask",
      "ipv6 expander",
      "ipv6 compression",
      "cidr to ip range",
      "ip to integer",
      "wildcard mask",
      "private ip ranges",
      "cidr split",
      "reverse dns arpa",
    ],
  },
  "/javascript": {
    label: "JavaScript",
    title: "Run JavaScript & TypeScript in the Browser",
    description:
      "Run a JavaScript or TypeScript script, or work at a REPL, on a WebAssembly engine served from this page — output as it is written and a panel of what the run left bound.",
    keywords: [
      "online javascript",
      "run javascript in browser",
      "javascript repl",
      "typescript playground",
      "javascript playground",
      "js console online",
      "quickjs",
      "webassembly javascript",
      "typescript repl",
      "run typescript online",
    ],
  },
  "/json": {
    label: "JSON",
    title: "JSON Formatter, Validator, Repair & JSONPath",
    description:
      "Format, minify, sort, repair and query JSON with JSONPath, without rounding a big number or dropping a duplicate key. The document stays in the browser.",
    keywords: [
      "json formatter",
      "json validator",
      "json beautifier",
      "json minifier",
      "json escape",
      "sort json keys",
      "pretty print json",
      "json editor",
      "jsonpath",
      "json repair",
      "json lines",
    ],
  },
  "/jwt": {
    label: "JWT",
    title: "JWT Decoder, Verifier & Builder",
    description:
      "Read a JSON Web Token's header and claims, check its signature or decrypt a JWE, and build, sign or encrypt a new one. Every key is made and used in the tab.",
    keywords: [
      "jwt decoder",
      "jwt debugger",
      "json web token",
      "verify jwt signature",
      "jwt generator",
      "jwe",
      "decrypt jwe",
      "encrypt jwt",
      "hs256",
      "rs256",
      "es256",
      "eddsa",
      "jwt claims",
    ],
  },
  "/keygen": {
    label: "Keygen",
    title: "SSH, PGP, age & JSON Web Key Generator",
    description:
      "Generate SSH, PGP, WireGuard and age keys — post-quantum ML-KEM included — plus JSON Web Keys and random secrets. The private half never leaves your browser.",
    keywords: [
      "ssh key generator",
      "pgp key generator",
      "json web key",
      "jwk generator",
      "wireguard keys",
      "age keygen",
      "age identity",
      "post-quantum key",
      "ml-kem-768",
      "ed25519 key",
      "rsa key generator",
      "ecdsa key",
    ],
  },
  "/markdown": {
    label: "Markdown",
    title: "Markdown Editor with Live Preview",
    description:
      "Write Markdown with a formatting bar and read it back rendered as you type, side by side or one at a time, in GitHub, CommonMark or original flavour.",
    keywords: [
      "markdown editor",
      "markdown preview",
      "live markdown preview",
      "markdown to html",
      "markdown to docx",
      "markdown to word",
      "github flavored markdown",
      "gfm",
      "commonmark",
      "readme editor",
      "markdown viewer",
    ],
  },
  "/mock": {
    label: "Mock Data",
    title: "Mock Data Generator from a JSON Schema",
    description:
      "Generate seeded rows of sample data from a JSON Schema, Zod or Pydantic model — names, emails, addresses, Luhn-valid cards, IBANs, ISBNs — as JSON, CSV or SQL.",
    keywords: [
      "mock data generator",
      "fake data generator",
      "test data generator",
      "sample data from json schema",
      "json schema to sample data",
      "zod mock data",
      "pydantic fake data",
      "seeded random data",
      "csv test data",
      "sql seed data generator",
      "luhn valid test card numbers",
      "iban generator",
      "isbn check digit",
      "luhn checker",
    ],
  },
  "/otp": {
    label: "OTP",
    title: "TOTP, HOTP & OCRA One-Time Password Calculator",
    description:
      "Work out a TOTP, HOTP or OCRA one-time password from a shared secret, with the algorithm, digits, counter and challenge the token uses. The secret never leaves the tab.",
    keywords: [
      "totp generator",
      "hotp calculator",
      "ocra calculator",
      "one-time password",
      "2fa code generator",
      "authenticator code",
      "rfc 6238",
      "rfc 4226",
      "rfc 6287",
      "base32 secret",
      "otpauth uri",
    ],
  },
  "/password": {
    label: "Password",
    title: "Password & Passphrase Generator",
    description:
      "Generate a strong random password at the length and mix of lowercase, uppercase, digits and symbols you want, or a memorable passphrase made of real English words.",
    keywords: [
      "password generator",
      "random password",
      "strong password",
      "secure password generator",
      "password length",
      "special characters password",
      "passphrase generator",
      "diceware",
      "memorable password",
      "random word password",
      "xkcd password",
      "secure passphrase",
      "word based password",
    ],
  },
  "/pdf": {
    label: "PDF",
    title: "PDF Viewer, Annotator & Signer",
    description:
      "Open a PDF to read it, or highlight, draw, add text, pictures and a signature, and save the marks into the file. Nothing is uploaded.",
    keywords: [
      "pdf viewer",
      "pdf annotator",
      "sign pdf online",
      "highlight pdf",
      "add text to pdf",
      "draw on pdf",
      "add image to pdf",
      "fill pdf form",
      "pdf editor",
    ],
  },
  "/phone-number": {
    label: "Phone Number",
    title: "Phone Number Validator & Lookup",
    description:
      "Parse a phone number against any of 245 dialling regions, see whether it is valid, where its range was issued, who carries it and what time it is there.",
    keywords: [
      "phone number validator",
      "libphonenumber",
      "e164 format",
      "phone number location lookup",
      "phone carrier lookup",
      "phone number time zone",
      "short code lookup",
      "international phone number format",
      "phone number parser",
      "rfc 3966 tel uri",
      "mobile or landline lookup",
    ],
  },
  "/python": {
    label: "Python",
    title: "Run Python in the Browser",
    description:
      "Run a Python script or work at a REPL with the standard library to hand, output as it is written and a panel of what the run left bound. No server and no install.",
    keywords: [
      "online python",
      "run python in browser",
      "python repl",
      "python playground",
      "pyodide",
      "python interpreter online",
      "webassembly python",
    ],
  },
  "/qr-code": {
    label: "QR Code",
    title: "WiFi, vCard & URL QR Code Generator",
    description:
      "Make a QR code for a link, a WiFi network, a contact card, an email, a phone number or an SMS, drawn as you type and saved as an SVG or a PNG. Nothing is fetched.",
    keywords: [
      "qr code generator",
      "wifi qr code",
      "vcard qr code",
      "url to qr code",
      "mailto qr code",
      "sms qr code",
      "contact card qr",
      "offline qr generator",
      "qr code svg",
    ],
  },
  "/regex": {
    label: "Regex",
    title: "Regex Tester & Explainer",
    description:
      "Write a regular expression, watch every match and capture group light up in your own text, and read a line-by-line breakdown of what the pattern does.",
    keywords: [
      "regex tester",
      "regular expression tester",
      "regex explainer",
      "regex debugger",
      "javascript regex",
      "named capture groups",
      "regex highlighter",
      "test regex online",
    ],
  },
  "/schema": {
    label: "Schema",
    title: "JSON Schema, Zod & Pydantic Validator",
    description:
      "Check a JSON payload against a JSON Schema, Zod or Pydantic model with every error marked in place, and convert a schema between all three.",
    keywords: [
      "json schema validator",
      "zod schema",
      "pydantic model",
      "json schema to zod",
      "zod to json schema",
      "pydantic to json schema",
      "json schema generator",
      "json to json schema",
      "schema converter",
    ],
  },
  "/sql": {
    label: "SQL",
    title: "SQLite & Postgres Playground",
    description:
      "Run SQL against a real SQLite or PostgreSQL database running entirely in your browser, with a schema tree, a results grid and a log of everything it did.",
    keywords: [
      "sql playground",
      "online sql editor",
      "sqlite in the browser",
      "postgres in the browser",
      "run sql online",
      "sql sandbox",
      "sqlite wasm",
      "pglite",
      "sql query tool",
    ],
  },
  "/string": {
    label: "String",
    title: "Case Converter, Line Sorter & Text Escaper",
    description:
      "Convert camel, snake, kebab, Pascal, title and sentence case, sort, dedupe, wrap and shuffle lines, slugify, count words, and escape for HTML, JS, C, shell and SQL.",
    keywords: [
      "case converter",
      "camel case converter",
      "snake case converter",
      "kebab case",
      "title case converter",
      "sort lines",
      "remove duplicate lines",
      "word count",
      "character count",
      "slugify text",
      "html entity encoder",
      "escape string",
      "text word wrap",
      "shuffle lines",
    ],
  },
  "/time": {
    label: "Time",
    title: "Timestamp, Duration & Time Zone Converter",
    description:
      "Convert a Unix timestamp across time zones and read it as ISO 8601 or RFC 2822, parse a duration like 1h 30m or PT1H30M, and count the days between two dates.",
    keywords: [
      "unix timestamp converter",
      "epoch converter",
      "time zone converter",
      "epoch to date",
      "iso 8601",
      "rfc 2822",
      "utc converter",
      "world clock",
      "duration parser",
      "iso 8601 duration",
      "date calculator",
      "days between dates",
      "add time to date",
    ],
  },
  "/unicode": {
    label: "Unicode",
    title: "Unicode Character & Code Point Inspector",
    description:
      "Names, categories, scripts, UTF-8, UTF-16 and UTF-32 bytes, every escape form, NFC/NFD/NFKC/NFKD, and the invisible, bidi and lookalike characters hiding in a text.",
    keywords: [
      "unicode inspector",
      "code point lookup",
      "character name lookup",
      "utf-8 bytes",
      "unicode escape",
      "unicode normalization",
      "nfc nfd nfkc nfkd",
      "invisible characters",
      "zero width space detector",
      "homoglyph detector",
      "trojan source",
      "special character picker",
      "insert special characters",
      "emoji picker",
    ],
  },
  "/unique-id": {
    label: "Unique ID",
    title: "UUID, ULID & NanoID Generator",
    description:
      "Generate UUIDs v1 through v8 and NanoID, CUID2, ULID, KSUID, XID, TypeID, MongoDB ObjectId, Firebase PushID, Snowflake and Sonyflake IDs, singly or by the batch.",
    keywords: [
      "uuid generator",
      "uuid v4",
      "uuid v7",
      "nanoid",
      "ulid generator",
      "cuid2",
      "ksuid",
      "typeid",
      "mongodb objectid",
      "snowflake id",
      "unique id generator",
    ],
  },
  "/url": {
    label: "URL",
    title: "URL Parser & Query String Builder",
    description:
      "Take a URL apart into scheme, host, port, path, query and fragment, and edit its query parameters unescaped in a builder that stays in step with the address.",
    keywords: [
      "url parser",
      "query string builder",
      "url decoder",
      "url encoder",
      "percent encoding",
      "query parameter editor",
      "url components",
      "parse url online",
      "url escape",
    ],
  },
  "/attributions": {
    label: "Attributions",
    title: "Open Source Attributions",
    description:
      "The licences and copyright notices of every open source package bundled into utils+, reproduced in full as their own terms ask for.",
    keywords: ["open source licences", "third party notices", "attributions", "licence texts"],
  },
} satisfies Record<string, PageMeta>;

export type PagePath = keyof typeof PAGE_META;

// Every page the navbar lists: all of them but the welcome page it sits beside and the attributions the footer links.
export type UtilityPath = Exclude<PagePath, typeof HOME_PATH | typeof ATTRIBUTIONS_PATH>;

export interface PageMeta {
  // What the navbar, the breadcrumb and the footer call the page, which is a word for somebody already on the site.
  label: string;
  // The headline of a search result, which the label alone would not be: "Codec" says nothing to somebody new.
  title: string;
  // What the page does and what it is good for, in the ~160 characters a result shows under that headline.
  description: string;
  // Read by the spotlight's search and by nothing that writes a head: the meta tag of that name has been ignored for
  // two decades, and a list of phrases a page wants to rank for is not something to hand every crawler that asks.
  keywords: string[];
  // Only ever set on a page that should not be indexed at all.
  noindex?: true;
}

// The utilities in the groups the footer and the welcome page list them under. Three dozen links in one run is a list
// nobody reads to the end, and the groups are by what somebody came to do rather than by how each page is built.
export const CATEGORIES: readonly Category[] = [
  { name: "Data", paths: ["/json", "/csv", "/config", "/schema", "/mock", "/unique-id", "/diff", "/markdown"] },
  { name: "Text and encoding", paths: ["/codec", "/string", "/unicode", "/regex", "/url", "/hex"] },
  { name: "Security", paths: ["/hasher", "/cryptography", "/keygen", "/jwt", "/certificate", "/otp", "/password"] },
  { name: "Network", paths: ["/curl", "/har", "/ip-address"] },
  { name: "Code", paths: ["/javascript", "/python", "/sql"] },
  { name: "Time and place", paths: ["/time", "/cron", "/countries", "/phone-number"] },
  { name: "Everyday", paths: ["/calculator", "/converter", "/colour", "/image", "/docx", "/pdf", "/qr-code"] },
];

export interface Category {
  name: string;
  paths: UtilityPath[];
}

// An address with no document of its own is answered by 404.html, which is written by hand rather than from these
// words; they are here so that what that page says and what this module says of a missing address is the one answer.
const NOT_FOUND_META: PageMeta = {
  label: "Not found",
  title: "Page Not Found",
  description: "No utility lives at this address. The list of everything utils+ can do is one click away.",
  keywords: [],
  noindex: true,
};

export function pageMeta(path: string): PageMeta {
  return PAGE_META[path as PagePath] ?? NOT_FOUND_META;
}

export function isPagePath(path: string): path is PagePath {
  return path in PAGE_META;
}

export function isUtilityPath(path: string): path is UtilityPath {
  return isPagePath(path) && path !== HOME_PATH && path !== ATTRIBUTIONS_PATH;
}

export function documentTitle(meta: PageMeta): string {
  return `${meta.title} · ${SITE_NAME}`;
}

// A share link's fragment is the state of the page and not another page, so the canonical is the path alone.
export function canonicalUrl(path: string): string {
  return `${SITE_ORIGIN}${path}`;
}

// One picture for every page, served at an address of its own rather than a hashed one, since the head that names it
// is written before the bundle has hashed anything. A card is what a link pasted into a chat or a forum turns into.
export const OG_IMAGE = {
  path: "/open-graph-image.png",
  width: 1200,
  height: 630,
  alt: "utils+: developer tools that run in your browser",
};

// One reading of a page's head, taken twice: the browser sets these on the head it was served, and the build writes
// them into the head it emits. Neither can drift from the other because neither spells the tags itself.
export function headMeta(path: string): HeadMeta {
  const meta = pageMeta(path);
  const title = documentTitle(meta);
  const url = canonicalUrl(path);
  const image = canonicalUrl(OG_IMAGE.path);

  return {
    title,
    canonical: url,
    metas: [
      { attribute: "name", key: "description", content: meta.description },
      { attribute: "name", key: "robots", content: meta.noindex ? "noindex, follow" : "index, follow" },
      { attribute: "property", key: "og:type", content: "website" },
      { attribute: "property", key: "og:site_name", content: SITE_NAME },
      { attribute: "property", key: "og:title", content: title },
      { attribute: "property", key: "og:description", content: meta.description },
      { attribute: "property", key: "og:url", content: url },
      { attribute: "property", key: "og:image", content: image },
      { attribute: "property", key: "og:image:width", content: String(OG_IMAGE.width) },
      { attribute: "property", key: "og:image:height", content: String(OG_IMAGE.height) },
      { attribute: "property", key: "og:image:alt", content: OG_IMAGE.alt },
      { attribute: "name", key: "twitter:card", content: "summary_large_image" },
      { attribute: "name", key: "twitter:title", content: title },
      { attribute: "name", key: "twitter:description", content: meta.description },
      { attribute: "name", key: "twitter:image", content: image },
    ],
  };
}

export interface HeadMeta {
  title: string;
  canonical: string;
  metas: MetaTag[];
}

export interface MetaTag {
  attribute: "name" | "property";
  key: string;
  content: string;
}

// Every address the site answers for, in the order they are written above: the welcome page, the utilities and
// attributions. The build writes a document and enters a page for each, and nothing else is a page of this site.
export function pagePaths(): PagePath[] {
  return Object.keys(PAGE_META) as PagePath[];
}

// The utilities, in the order they are written above: every page the navbar lists.
export function utilityPaths(): UtilityPath[] {
  return pagePaths().filter(isUtilityPath);
}

// Where a page is written, relative to src: the build enters it by this name and writes its chunk into the document.
export function pageModule(path: PagePath): string {
  if (path === HOME_PATH) return "welcome.tsx";
  if (path === ATTRIBUTIONS_PATH) return "attributions.tsx";
  const name = path.slice(1);
  return `utilities/${name}/${name}.tsx`;
}

// What a static host serves a path from: /codec is codec/index.html, the directory index of the address itself, which
// every CDN answers with unasked. A flat codec.html would need a rewrite rule to reach and would leave /codec.html
// resolving as a second address for the same page. The welcome page is index.html at the root and is never copied.
export function documentFileName(path: PagePath): string {
  return path === HOME_PATH ? "index.html" : `${path.slice(1)}/index.html`;
}

// The pages worth crawling, in the order they are written above: the welcome page, the utilities, then attributions.
export function indexablePaths(): PagePath[] {
  return pagePaths().filter((path) => !pageMeta(path).noindex);
}

// When each page last changed is the build's to find out and not this module's, which cannot ask git anything; a page
// it has no date for is listed without one, since a date that is only the build's own trains a crawler to ignore it.
export function sitemapXml(lastModified: Partial<Record<PagePath, string>> = {}): string {
  const urls = indexablePaths().map((path) => {
    const modified = lastModified[path];
    const lines = [`    <loc>${escapeHtml(canonicalUrl(path))}</loc>`];
    if (modified) lines.push(`    <lastmod>${escapeHtml(modified)}</lastmod>`);
    return ["  <url>", ...lines, "  </url>"].join("\n");
  });

  return [
    "<?xml version=\"1.0\" encoding=\"UTF-8\"?>",
    "<urlset xmlns=\"http://www.sitemaps.org/schemas/sitemap/0.9\">",
    ...urls,
    "</urlset>",
    "",
  ].join("\n");
}

// Nothing here is behind a login and no path is private, so the file exists to name the sitemap.
export function robotsTxt(): string {
  return [
    "User-agent: *",
    "Allow: /",
    "",
    `Sitemap: ${SITE_ORIGIN}/sitemap.xml`,
    "",
  ].join("\n");
}

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
